import type {
  AtlasCanvasItemRow,
  AtlasPoint,
} from "@/components/ecv2/components/whiteboardEC/schema/canvas/canvasTypes";

export type AtlasRotatedRect = Pick<
  AtlasCanvasItemRow,
  "x" | "y" | "width" | "height" | "rotation"
>;

export function getAtlasItemCenter(item: AtlasRotatedRect): AtlasPoint {
  return { x: item.x + item.width / 2, y: item.y + item.height / 2 };
}

export function rotateAtlasPointAround(
  point: AtlasPoint,
  center: AtlasPoint,
  rotationDeg: number
): AtlasPoint {
  if (rotationDeg === 0) {
    return { ...point };
  }
  const radians = (rotationDeg * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cosine - dy * sine,
    y: center.y + dx * sine + dy * cosine,
  };
}

export function getAtlasItemWorldPoint(item: AtlasRotatedRect, point: AtlasPoint): AtlasPoint {
  return rotateAtlasPointAround(point, getAtlasItemCenter(item), item.rotation ?? 0);
}

export function getAtlasItemUnrotatedPoint(item: AtlasRotatedRect, point: AtlasPoint): AtlasPoint {
  return rotateAtlasPointAround(point, getAtlasItemCenter(item), -(item.rotation ?? 0));
}

export function getAtlasItemAxisAlignedBounds(item: AtlasRotatedRect): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const right = item.x + item.width;
  const bottom = item.y + item.height;
  if ((item.rotation ?? 0) === 0) {
    const x = Math.min(item.x, right);
    const y = Math.min(item.y, bottom);
    return { x, y, width: Math.max(item.x, right) - x, height: Math.max(item.y, bottom) - y };
  }
  const centerX = item.x + item.width / 2;
  const centerY = item.y + item.height / 2;
  const radians = ((item.rotation ?? 0) * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const leftCosine = (item.x - centerX) * cosine;
  const rightCosine = (right - centerX) * cosine;
  const topSine = (item.y - centerY) * sine;
  const bottomSine = (bottom - centerY) * sine;
  const leftSine = (item.x - centerX) * sine;
  const rightSine = (right - centerX) * sine;
  const topCosine = (item.y - centerY) * cosine;
  const bottomCosine = (bottom - centerY) * cosine;
  const x = centerX + Math.min(leftCosine, rightCosine) - Math.max(topSine, bottomSine);
  const y = centerY + Math.min(leftSine, rightSine) + Math.min(topCosine, bottomCosine);
  const maxX = centerX + Math.max(leftCosine, rightCosine) - Math.min(topSine, bottomSine);
  const maxY = centerY + Math.max(leftSine, rightSine) + Math.max(topCosine, bottomCosine);
  return { x, y, width: maxX - x, height: maxY - y };
}

export function isAtlasPointNearItem(
  point: AtlasPoint,
  item: AtlasRotatedRect,
  margin: number
): boolean {
  const localPoint = getAtlasItemUnrotatedPoint(item, point);
  return (
    localPoint.x >= item.x - margin &&
    localPoint.x <= item.x + item.width + margin &&
    localPoint.y >= item.y - margin &&
    localPoint.y <= item.y + item.height + margin
  );
}
